import { computed, inject, Injectable } from "@angular/core";
import { TranslateService } from "@ngx-translate/core";
import { DateHelper, DateInput } from "../class/general-classes/date-helper";

/**
 * Ways of showing a date to the user. Each one says which fields appear; their order, the separators, the names
 * of months and days and the 12 or 24 hour clock come from the locale.
 */
export type DateStyle =
  | "day"
  | "date"
  | "dateMedium"
  | "dateShortYear"
  | "dateTimeShortYear"
  | "dateTime"
  | "dateTimeSeconds"
  | "time"
  | "timeSeconds"
  | "dayMonth"
  | "dayMonthTime"
  | "weekdayDayMonth"
  | "weekdayDayMonthShort"
  | "weekdayDayMonthShortTime"
  | "weekdayDayMonthLongTime"
  | "dayMonthShort"
  | "monthYear"
  | "month"
  | "monthShort"
  | "weekday"
  | "weekdayShort"
  | "weekdayDateMedium"
  | "year"
  | "dateLong"
  | "dateTimeLong"
  | "dateTimeSecondsLong";

const TWO_DIGIT_DATE: Intl.DateTimeFormatOptions = { day: "2-digit", month: "2-digit", year: "numeric" };
// Two digits for the hour with the 24-hour clock ("09:05"); formatDateIn switches to one digit where the locale
// uses the 12-hour clock ("9:05 AM")
const TIME: Intl.DateTimeFormatOptions = { hour: "2-digit", minute: "2-digit" };
const LONG_DATE: Intl.DateTimeFormatOptions = { day: "numeric", month: "long", year: "numeric" };

const MEDIUM_DATE: Intl.DateTimeFormatOptions = { day: "numeric", month: "short", year: "numeric" };

const DATE_STYLES: Record<DateStyle, Intl.DateTimeFormatOptions> = {
  day: { day: "numeric" },
  date: TWO_DIGIT_DATE,
  dateMedium: MEDIUM_DATE,
  dateShortYear: { day: "2-digit", month: "2-digit", year: "2-digit" },
  dateTimeShortYear: { day: "2-digit", month: "2-digit", year: "2-digit", ...TIME },
  dateTime: { ...TWO_DIGIT_DATE, ...TIME },
  dateTimeSeconds: { ...TWO_DIGIT_DATE, ...TIME, second: "2-digit" },
  time: TIME,
  timeSeconds: { ...TIME, second: "2-digit" },
  dayMonth: { day: "2-digit", month: "2-digit" },
  dayMonthTime: { day: "2-digit", month: "2-digit", ...TIME },
  weekdayDayMonth: { weekday: "short", day: "2-digit", month: "2-digit" },
  weekdayDayMonthShort: { weekday: "short", day: "numeric", month: "short" },
  weekdayDayMonthShortTime: { weekday: "short", day: "numeric", month: "short", ...TIME },
  weekdayDayMonthLongTime: { weekday: "long", day: "numeric", month: "long", ...TIME },
  dayMonthShort: { day: "numeric", month: "short" },
  monthYear: { month: "long", year: "numeric" },
  month: { month: "long" },
  monthShort: { month: "short" },
  weekday: { weekday: "long" },
  weekdayShort: { weekday: "short" },
  weekdayDateMedium: { weekday: "long", ...MEDIUM_DATE },
  year: { year: "numeric" },
  dateLong: LONG_DATE,
  dateTimeLong: { ...LONG_DATE, ...TIME },
  dateTimeSecondsLong: { ...LONG_DATE, ...TIME, second: "2-digit" },
};

/**
 * Locale of the user, for the dates and numbers shown on screen. It follows the language of the application, so
 * it changes at once when the user picks another language, without reloading anything.
 */
@Injectable({ providedIn: "root" })
export class LocaleService {
  private static readonly _formatters = new Map<string, Intl.DateTimeFormat>();
  private readonly _translateService = inject(TranslateService);

  /** Language of the application, `en` until the first one is set. */
  readonly language = computed(() => this._translateService.currentLang() ?? "en");

  /**
   * Locale of the application: its language with the region of the device when the device uses the same language
   * (`en-GB` on a British phone, `it-CH` in Ticino), the language alone otherwise.
   */
  readonly locale = computed(() => LocaleService.localeOf(this.language(), navigator.languages));

  /** First day of the week in the language of the application: 0 is Sunday, 1 is Monday. */
  readonly firstDayOfWeek = computed(() => DateHelper.firstDayOfWeek(this.language()));

  /**
   * Writes a date for the user, in the current locale.
   *
   * @param value date to show; strings are read by {@link DateHelper.toDate}
   * @param style fields to show
   * @returns the text, empty when the value is missing or is not a valid date
   */
  formatDate(value: DateInput | null | undefined, style: DateStyle): string {
    return LocaleService.formatDateIn(this.locale(), value, style);
  }

  /**
   * Writes a date for the user in a given locale, for the classes that do not receive the service.
   *
   * @param locale locale, usually {@link LocaleService.locale}
   * @param value date to show; strings are read by {@link DateHelper.toDate}
   * @param style fields to show
   * @returns the text, empty when the value is missing or is not a valid date
   */
  static formatDateIn(locale: string, value: DateInput | null | undefined, style: DateStyle): string {
    if (value === null || value === undefined || value === "") {
      return "";
    }
    const date = DateHelper.toDate(value);
    if (!DateHelper.isValid(date)) {
      return "";
    }
    const key = locale + "|" + style;
    let formatter = LocaleService._formatters.get(key);
    if (!formatter) {
      const options = { ...DATE_STYLES[style] };
      if (options.hour && new Intl.DateTimeFormat(locale, { hour: "numeric" }).resolvedOptions().hour12) {
        options.hour = "numeric";
      }
      formatter = new Intl.DateTimeFormat(locale, options);
      LocaleService._formatters.set(key, formatter);
    }
    return formatter.format(date);
  }

  /**
   * Locale for a language of the application, taking the region from the locales of the device.
   *
   * @param language language of the application, such as `it`
   * @param deviceLocales locales of the device, in order of preference
   */
  static localeOf(language: string, deviceLocales: readonly string[]): string {
    const prefix = language.toLowerCase() + "-";
    return deviceLocales?.find((locale) => locale.toLowerCase().startsWith(prefix)) ?? language;
  }
}
